// Host transport for the CDN-hosted GENERIC widget, embedded in the first-party
// portal. Implements the widget's transport contract (`init`, an
// async-generator `stream`, and the OPTIONAL live-agent methods `startLive` /
// `sendLive`) against our SAME-ORIGIN foundry endpoints:
//   - POST /api/v1/chat/init    (session init -> server ID + greeting)
//   - POST /api/v1/chat/stream  (SSE: start / token / done / error; also live)
//
// It mirrors partner/ocms/src/ocmsTransport.js (the reference HOST transport) but
// is wired to the SPA's MSAL identity via AuthArgs, so signed-in turns carry the
// bearer + identity hints EXACTLY like the built-in chatClient. Same-origin, so
// no CORS. The widget core stays presentation-only; this is host code.
//
// Live mode: after the AI hands off (`channelAction: handoff_to_omnichannel`),
// the widget calls startLive(). We long-poll POST /chat/stream with
// { message:'', conversationId, poll:true } and translate the server's live SSE
// frames (mode:'live'/'ended'/'ai', senderKind, liveCategory) into the widget's
// CmspChatLiveFrame shape ({ kind:'agent'|'system'|'ended', text, category }).
// sendLive() also returns any live frames drained by its response.

import type { AuthArgs } from './authArgs';
import { collectClientContext } from './clientContext';
import { deriveCoarseRegion } from './region';
import { createPreviewConversationTransport } from './previewConversationTransport';
import type { CmspChatInit, CmspCaseDetails } from '../../../../../../partner/widget/src/contract';
import { withOmnichannelTransport } from '../../../../../../partner/ocms/src/omnichannelCoordinator.js';
import { createConversationTraceparent } from '../../../../../../partner/ocms/src/conversationTrace.js';

/** One streamed chunk in the widget's transport contract. */
export interface WidgetChunk {
  clientOperationId?: string;
  error?: string;
  code?: string;
  readSequence?: number;
  readOn?: string;
  liveCapabilities?: { incomingReadReceipts: boolean; sendReadReceipts: boolean };
  /** On 'text': optional localization template and opaque positional arguments. */
  messageKey?: string | null;
  messageArgs?: readonly string[] | null;
  caseDetails?: CmspCaseDetails | null;
  messageId?: string | null;
  senderName?: string | null;
  type: 'text' | 'action' | 'done' | 'error' | 'live' | 'prompt';
  data: string;
  /** On 'live': whether a human authored the frame or it is a lifecycle notice. */
  liveKind?: 'agent' | 'bot' | 'system';
  recoveryState?: 'recovering' | 'active' | 'auth-failed' | 'failed';
  /** On 'live' + liveKind 'system': the server's lifecycle category. */
  liveCategory?: string;
  /**
   * On 'prompt': the structured input the server is asking for. The widget renders it and answers by
   * calling `submitPrompt`, so escalation steps work here without any escalation-specific endpoint.
   */
  prompt?: WidgetPrompt;
  meta?: { turnId?: string; feedbackEligible?: boolean; citations?: unknown[]; trace?: string; liveSession?: boolean; retryMessage?: boolean; redirectUrl?: string; mode?: string | null; sessionId?: string | null; liveSdkEnabled?: boolean | null; liveSdkDisabledReason?: string | null };
}

/** A server-issued request for structured input (mirrors ChatPrompt on the stream contract). */
export interface WidgetPrompt {
  kind: 'choice' | 'form' | 'adaptive-card';
  /** Server-owned action id. Echoed back verbatim; the widget never resolves or interprets it. */
  verb: string;
  /** Single-use token. Echoed back verbatim; a replay is rejected server-side. */
  stepToken: string;
  text?: string | null;
  messageKey?: string | null;
  options?: {
    id: string;
    label: string;
    description?: string | null;
    redirectUrl?: string | null;
    isAvailable?: boolean;
    unavailableReason?: string | null;
    detail?: string | null;
    group?: string | null;
  }[] | null;
  fields?: {
    id: string;
    label: string;
    type: 'text' | 'tel' | 'choice';
    placeholder?: string | null;
    isRequired?: boolean;
    defaultValue?: string | null;
    options?: { id: string; label: string }[] | null;
    maxLength?: number | null;
    errorMessage?: string | null;
    errorMessageKey?: string | null;
  }[] | null;
  card?: unknown | null;
}

/** One live-agent frame (matches the widget's CmspChatLiveFrame). */
export interface WidgetLiveFrame {
  clientOperationId?: string;
  error?: string;
  readSequence?: number;
  readOn?: string;
  liveCapabilities?: { incomingReadReceipts: boolean; sendReadReceipts: boolean };
  messageUpdated?: boolean;
  messageId?: string | null;
  senderName?: string | null;
  kind: 'agent' | 'bot' | 'system' | 'prechat_survey' | 'ended' | 'retry';
  recoveryState?: 'recovering' | 'active' | 'auth-failed' | 'failed';
  text: string;
  category?: string;
  /** On 'prechat_survey': the Adaptive Card JSON to render before going live. */
  survey?: unknown;
  /** On 'ended': optional post-chat survey URL to offer the customer. */
  surveyUrl?: string;
}

/** One turn of a server-assembled transcript (mirrors the widget's CmspChatTranscriptEntry). */
export interface WidgetTranscriptEntry {
  role: 'user' | 'agent' | 'system';
  text: string;
  isLiveAgent?: boolean;
  at?: string | number;
  citations?: { title: string; url: string; snippet?: string }[];
}

/** A server-assembled transcript (mirrors the widget's CmspChatTranscript). */
export interface WidgetTranscript {
  conversationId?: string;
  entries: WidgetTranscriptEntry[];
  source?: string;
  truncated?: boolean;
  citationsAvailable?: boolean;
}

/** The subset of the widget's CmspChatTransport this host implements. */
export interface WidgetTransport {
  markRead(conversationId: string, messageId: string): Promise<void>;
  endLive(conversationId: string): Promise<void>;
  detach(conversationId?: string): void;
  getActiveConversationId(): string | undefined;
  hasBrowserSession(): boolean;
  dispose(conversationId?: string): Promise<void>;
  init(conversationId: string): Promise<CmspChatInit>;
  stream(message: string, conversationId: string, signal?: AbortSignal, clientOperationId?: string): AsyncGenerator<WidgetChunk>;
  /**
   * Answers a `prompt` chunk. Posts a promptResponse turn to the SAME chat stream endpoint as
   * `stream`, so escalation steps need no escalation-specific route here — the widget only echoes the
   * server-issued verb and step token back.
   */
  submitPrompt(
    prompt: WidgetPrompt,
    values: Record<string, string | boolean>,
    conversationId: string,
    signal?: AbortSignal,
  ): AsyncGenerator<WidgetChunk>;
  startLive(conversationId: string, signal?: AbortSignal): AsyncGenerator<WidgetLiveFrame>;
  sendLive(message: string, conversationId: string, signal?: AbortSignal, clientOperationId?: string): Promise<WidgetLiveFrame[]>;
  submitPreChatSurvey(
    answers: Record<string, unknown>,
    conversationId: string,
    signal?: AbortSignal,
  ): AsyncGenerator<WidgetLiveFrame>;
  /**
   * Fetches the server-side transcript for the download control. The API assembles it from CMSP
   * storage, so it covers turns this page view never rendered AND the human live-agent leg of a
   * handed-off conversation — neither of which the browser holds. Resolves null when there is no
   * server record, which the widget treats as "use what is on screen".
   */
  fetchTranscript(conversationId: string, signal?: AbortSignal): Promise<WidgetTranscript | null>;
  /**
   * HOST-ONLY (outside the widget contract — the core never calls it). Re-runs /chat/init on the
   * SAME conversation when the signed-in user changes, mirroring the built-in ChatWidget's
   * mid-conversation sign-in effect. Re-running the core's init() instead would reset its message
   * list, so the refresh has to happen here.
   */
  refreshIdentity(): Promise<void>;
}

export interface CdnWidgetTransportOptions {
  /** Routing id sent as X-CMSP-Entry-Point (e.g. the selected agent's entryPoint). */
  entryPoint: string;
  /**
   * Entra client id of the hosting SPA, forwarded as `callerAppId` on /chat/init
   * (mirrors the built-in chatClient's `cfg.clientId`). Falls back to 'unknown'.
   */
  callerAppId?: string;
  /** Fallback greeting if /init is unavailable. */
  greeting?: string;
  /** Resolves the signed-in user's MSAL bearer + identity hints, or null when anonymous. */
  getAuthArgs: () => Promise<AuthArgs | null>;
}

const ANON_SESSION_KEY = 'cmsp.anon.session';
/** Gentle backoff between live polls that returned no new frames (server long-polls). */
const LIVE_EMPTY_POLL_DELAY_MS = 1000;

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable per-browser anonymous session id; degrades gracefully without storage/crypto. */
function getAnonSessionId(): string {
  try {
    let id = localStorage.getItem(ANON_SESSION_KEY);
    if (!id) {
      id = newSessionId();
      localStorage.setItem(ANON_SESSION_KEY, id);
    }
    return id;
  } catch {
    return newSessionId();
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

interface FoundryEvent {
  caseDetails?: CmspCaseDetails | null;
  liveSdkEnabled?: boolean | null;
  liveSdkDisabledReason?: string | null;
  messageKey?: string | null;
  messageArgs?: readonly string[] | null;
  messageId?: string | null;
  senderName?: string | null;
  type?: string;
  text?: string;
  channelAction?: string;
  turnId?: string;
  feedbackEligible?: boolean;
  citations?: unknown[];
  error?: string;
  /** Server-rendered timing waterfall (Development / EmitTurnTrace only); drives `/duration`. */
  trace?: string;
  // Live-agent (human handoff) fields.
  mode?: string;
  senderKind?: string;
  liveCategory?: string;
  recoveryState?: 'recovering' | 'active' | 'auth-failed' | 'failed';
  /** Files the live agent attached; the widget contract is text-only so these are named in the text. */
  attachments?: Array<{ fileId: string; fileName?: string | null; contentType?: string | null }>;
  sessionId?: string;
  /** Pre-chat survey Adaptive Card, present when mode === 'prechat-survey'. */
  preChatSurvey?: unknown;
  /** Post-chat (Customer Voice) survey URL on the terminal done of a live session. */
  postChatSurveyUrl?: string;
  /**
   * True on a live `done` when the customer's message could NOT be delivered (the
   * OC session was still warming up on another replica) — the widget should
   * re-send the same message once.
   */
  retryMessage?: boolean;
  /** Structured input the server is asking for; answered by posting a promptResponse turn. */
  prompt?: WidgetPrompt;
  /** Redirect link for a chosen non-agent modality. Distinct from the diagnostic `trace`. */
  redirectUrl?: string;
}

/** Parse one SSE frame (event:/data: lines) into a FoundryEvent, or null if empty. */
function parseSseFrame(frame: string): FoundryEvent | null {
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const rawLine of frame.split('\n')) {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  const data = dataLines.join('\n');
  if (!data) return null;
  try {
    const parsed = JSON.parse(data) as FoundryEvent;
    return { ...parsed, type: parsed.type ?? eventName };
  } catch {
    return null;
  }
}

/** Read an SSE Response body and yield every parsed FoundryEvent until the stream closes. */
async function* readFoundryEvents(res: Response, signal?: AbortSignal): AsyncGenerator<FoundryEvent> {
  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    signal?.throwIfAborted();
    if (done) {
      buffer += decoder.decode();
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      signal?.throwIfAborted();
      const evt = parseSseFrame(frame);
      if (evt) yield evt;
    }
  }
  if (buffer.trim()) {
    signal?.throwIfAborted();
    const evt = parseSseFrame(buffer);
    if (evt) yield evt;
  }
}

export function createCdnWidgetTransport(options: CdnWidgetTransportOptions): WidgetTransport {
  return createPreviewConversationTransport(async (id, signal) => {
    const { initialize, bind, setSignal } = createConversationTransport(options, signal);
    const response = await initialize(id, signal);
    signal.throwIfAborted();
    return { id: response.conversationId, transport: bind(response), setSignal };
  });
}

function createConversationTransport(options: CdnWidgetTransportOptions, conversationSignal: AbortSignal) {
  const {
    entryPoint,
    callerAppId = 'unknown',
    greeting = 'Hi! I can help with Microsoft account and sign-in questions.',
    getAuthArgs,
  } = options;

  async function resolveAuthArgs(): Promise<AuthArgs | null> {
    try {
      return await getAuthArgs();
    } catch {
      return null;
    }
  }

  // Identity state cached by /chat/init, mirroring the built-in ChatWidget's `initChatDone`.
  let initConversationId: string | null = null;
  let initHomeAccountId: string | null = null;
  let initLiveSdkEnabled: boolean | undefined;

  async function chatHeaders(conversationId?: string): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-CMSP-Anonymous-Session-Id': getAnonSessionId(),
      'X-CMSP-Entry-Point': entryPoint,
      'Accept-Language': navigator.language || 'en-US',
    };
    if (conversationId) {
      headers['X-CMSP-Conversation-Id'] = conversationId;
      // One trace per conversation: a new chat (new conversation id) starts a new trace.
      headers['traceparent'] = createConversationTraceparent(conversationId)!;
    }
    const auth = await resolveAuthArgs();
    if (auth) {
      headers['Authorization'] = `Bearer ${auth.authBearer}`;
      // Identity hints are the legacy fallback for when /chat/init hasn't cached this identity
      // server-side yet (init still pending, or it failed) — same gate as chatClient's `useInitChat`.
      if (initHomeAccountId !== auth.homeAccountId) {
        headers['X-CMSP-User-HomeAccountId'] = auth.homeAccountId;
        if (auth.username) headers['X-CMSP-User-Username'] = auth.username;
        if (auth.displayName) headers['X-CMSP-User-DisplayName'] = auth.displayName;
      }
    }
    return headers;
  }

  /** POSTs /chat/init for `conversationId` and records the identity it cached. */
  async function postInit(conversationId: string, signal = conversationSignal): Promise<CmspChatInit & { conversationId: string }> {
    // Resolve identity first so /init records the correct app + auth state
    // (callerAppId = the SPA's Entra client id; isAuthenticated + identity
    // hints mirror the built-in chatClient's InitChatRequest body).
    const auth = await resolveAuthArgs();
    const headers = await chatHeaders(conversationId);
    signal?.throwIfAborted();
    const res = await fetch('/api/v1/chat/init', {
      method: 'POST',
      headers,
      signal,
      body: JSON.stringify({
        conversationId,
        sourceAppName: 'portal-cdn-widget',
        liveSdkSupported: true,
        callerAppId,
        locale: navigator.language || 'en-US',
        region: deriveCoarseRegion(),
        entryPoint,
        isAuthenticated: auth != null,
        ...(auth
          ? {
              homeAccountId: auth.homeAccountId,
              ...(auth.username ? { email: auth.username } : {}),
              ...(auth.displayName ? { displayName: auth.displayName } : {}),
            }
          : {}),
      }),
    });
    if (!res.ok) throw new Error(`chat_init_${res.status}`);
    const body = (await res.json()) as CmspChatInit & { conversationId?: string };
    signal?.throwIfAborted();
    const canonicalId = body.conversationId ?? conversationId;
    if (typeof canonicalId !== 'string' || !canonicalId.trim()) throw new Error('chat_init_invalid_conversation_id');
    initConversationId = canonicalId;
    initHomeAccountId = auth?.homeAccountId ?? null;
    initLiveSdkEnabled = body.liveSdkEnabled;
    return { ...body, conversationId: canonicalId };
  }

  function chunksFor(evt: FoundryEvent): WidgetChunk[] {
    switch (evt.type) {
      case 'token':
        // A live frame forwarded on the AI stream is a human agent's message, not an
        // assistant token — route it like the built-in chatClient's onLiveToken so it
        // is never appended to the reply nor timed as time-to-first-token.
        if (evt.mode === 'live') {
          // A file-only frame has no text; name the files so it is not dropped as an empty frame.
          const attachmentNames = (evt.attachments ?? []).map(a => a.fileName?.trim() || 'file').join(', ');
          const data = evt.text
            ? (attachmentNames ? `${evt.text}\n\n📎 ${attachmentNames}` : evt.text)
            : (attachmentNames ? `📎 ${attachmentNames}` : '');
          return [{
            type: 'live',
            data,
            liveKind: evt.senderKind === 'human' ? 'agent' : evt.senderKind === 'bot' ? 'bot' : 'system',
            messageId: evt.messageId,
            senderName: evt.senderName,
            recoveryState: evt.recoveryState,
            ...(evt.liveCategory ? { liveCategory: evt.liveCategory } : {}),
          }];
        }
        // Yield even a text-less token frame so the widget stamps time-to-first-token on the
        // SAME frame as the built-in assistant (chatClient calls onToken(event.text ?? '')).
        return [{
          type: 'text',
          data: evt.text ?? '',
          ...(evt.messageKey ? { messageKey: evt.messageKey, messageArgs: evt.messageArgs } : {}),
          ...(evt.caseDetails ? { caseDetails: evt.caseDetails } : {}),
        }];
      case 'done': {
        const out: WidgetChunk[] = [];
        const meta: WidgetChunk['meta'] = {};
        const liveSdkEnabled = evt.liveSdkEnabled !== undefined
          ? evt.liveSdkEnabled
          : initLiveSdkEnabled;
        if (liveSdkEnabled !== undefined) meta.liveSdkEnabled = liveSdkEnabled;
        if (evt.liveSdkDisabledReason !== undefined) meta.liveSdkDisabledReason = evt.liveSdkDisabledReason;
        if (evt.mode !== undefined) meta.mode = evt.mode;
        if (evt.sessionId !== undefined) meta.sessionId = evt.sessionId;
        if (evt.channelAction) out.push({ type: 'action', data: evt.channelAction, ...(Object.keys(meta).length ? { meta: { ...meta } } : {}) });
        if (evt.turnId != null) meta.turnId = evt.turnId;
        if (evt.feedbackEligible != null) meta.feedbackEligible = evt.feedbackEligible;
        if (Array.isArray(evt.citations) && evt.citations.length > 0) meta.citations = evt.citations;
        if (evt.trace) meta.trace = evt.trace;
        if (evt.redirectUrl) meta.redirectUrl = evt.redirectUrl;
        // SDK-ready turns can enter browser live mode before bootstrap returns a session.
        if (evt.mode === 'live' && (evt.sessionId || evt.retryMessage === true)) meta.liveSession = true;
        if (evt.mode === 'live' && evt.retryMessage === true) meta.retryMessage = true;
        // Slash-command replies come back whole on `done` with no tokens; pass the
        // text along so the widget can fall back to it (same as chatClient onDone).
        const doneText = evt.text ?? '';
        out.push(Object.keys(meta).length > 0 ? { type: 'done', data: doneText, meta } : { type: 'done', data: doneText });
        // Emitted AFTER 'done' so the widget has rendered the turn's text before it renders the
        // control that answers it.
        if (evt.prompt) out.push({ type: 'prompt', data: evt.prompt.text ?? '', prompt: evt.prompt });
        return out;
      }
      case 'error':
        return [{ type: 'error', data: evt.error ?? 'Unknown SSE error' }];
      default:
        return [];
    }
  }

  return {
    initialize: postInit,
    bind,
    setSignal: (signal: AbortSignal) => { conversationSignal = signal; },
  };

  function bind(initial: CmspChatInit & { conversationId: string }): WidgetTransport {
    let prepared: CmspChatInit | undefined = initial;
    async function fetchChat(url: string, request: RequestInit): Promise<Response> {
      request.signal?.throwIfAborted();
      const response = await fetch(url, request);
      request.signal?.throwIfAborted();
      return response;
    }
    return withOmnichannelTransport({
    async init(conversationId: string): Promise<CmspChatInit> {
      const body = prepared ?? await postInit(conversationId);
      prepared = undefined;
      return { ...body, greeting: body.greeting ?? greeting };
    },

    async refreshIdentity(): Promise<void> {
      if (!initConversationId) return;
      const auth = await resolveAuthArgs();
      if ((auth?.homeAccountId ?? null) === initHomeAccountId) return;
      // Re-identify the server-issued conversation, not the widget's proposed ID.
      await postInit(initConversationId);
    },

    async fetchTranscript(conversationId: string, signal?: AbortSignal): Promise<WidgetTranscript | null> {
      const res = await fetchChat(
        `/api/v1/chat/${encodeURIComponent(conversationId)}/transcript`,
        { method: 'GET', headers: await chatHeaders(conversationId), signal },
      );
      // 404 covers both "no such conversation" and "not yours" (the API refuses to confirm which),
      // so null here means "nothing server-side to offer" and the widget saves what it rendered.
      if (res.status === 404) return null;
      if (!res.ok) {
        throw new Error(`transcript request failed: ${res.status}`);
      }
      const body = (await res.json()) as WidgetTranscript | null;
      return body && Array.isArray(body.entries) ? body : null;
    },

    async *stream(message: string, conversationId: string, signal?: AbortSignal): AsyncGenerator<WidgetChunk> {
      const res = await fetchChat('/api/v1/chat/stream', {
        method: 'POST',
        headers: await chatHeaders(conversationId),
        body: JSON.stringify({
          message,
          conversationId,
          clientContext: await collectClientContext(conversationId),
        }),
        signal,
      });

      if (!res.ok) {
        throw new Error(`chat stream failed: ${res.status}`);
      }

      // A stream ending with no terminal chunk is detected by the widget core,
      // which surfaces its own localized notice — don't synthesize one here.
      for await (const evt of readFoundryEvents(res, signal)) {
        for (const chunk of chunksFor(evt)) {
          yield chunk;
        }
      }
    },

    // Answers a server-issued prompt. Same endpoint and same chunk translation as `stream` — the only
    // difference is the turn payload, which is what makes escalation work here without the widget
    // learning any escalation-specific route or request shape.
    async *submitPrompt(
      prompt: WidgetPrompt,
      values: Record<string, string | boolean>,
      conversationId: string,
      signal?: AbortSignal,
    ): AsyncGenerator<WidgetChunk> {
      const res = await fetchChat('/api/v1/chat/stream', {
        method: 'POST',
        headers: await chatHeaders(conversationId),
        body: JSON.stringify({
          message: '',
          conversationId,
          promptResponse: { verb: prompt.verb, stepToken: prompt.stepToken, values },
        }),
        signal,
      });

      if (!res.ok) {
        throw new Error(`prompt submit failed: ${res.status}`);
      }

      for await (const evt of readFoundryEvents(res, signal)) {
        for (const chunk of chunksFor(evt)) {
          yield chunk;
        }
      }
    },

    // Live-agent receive loop: long-poll the same /chat/stream endpoint with a
    // message-less "drain" turn and translate live SSE frames into live frames.
    async *startLive(conversationId: string, signal?: AbortSignal): AsyncGenerator<WidgetLiveFrame> {
      while (!signal?.aborted) {
        let res: Response;
        try {
          res = await fetchChat('/api/v1/chat/stream', {
            method: 'POST',
            headers: await chatHeaders(conversationId),
            body: JSON.stringify({ message: '', conversationId, poll: true }),
            signal,
          });
        } catch (err) {
          if (signal?.aborted) return;
          throw err;
        }

        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn('[cdn-widget] live poll failed:', res.status);
          yield { kind: 'ended', text: '', category: 'unavailable' };
          return;
        }

        let ended = false;
        let sawFrame = false;
        for await (const evt of readFoundryEvents(res, signal)) {
          // A pre-chat survey must be answered before the session goes live.
          // Yield it and STOP polling; the widget renders the card and, on
          // submit, restarts the loop (which then drains the live thread).
          if (evt.mode === 'prechat-survey' && evt.preChatSurvey != null) {
            yield { kind: 'prechat_survey', text: '', survey: evt.preChatSurvey };
            return;
          }
          if (evt.type === 'token' && evt.mode === 'live') {
            sawFrame = true;
            if (evt.senderKind === 'human' || evt.senderKind === 'bot') {
              // The CDN widget's frame contract carries text only, so a file cannot be rendered as a
              // download chip here. Name it in the text instead — an empty bubble would look like a bug.
              const attachmentNames = (evt.attachments ?? [])
                .map(a => a.fileName?.trim() || 'file')
                .join(', ');
              const text = evt.text
                ? (attachmentNames ? `${evt.text}\n\n📎 ${attachmentNames}` : evt.text)
                : (attachmentNames ? `📎 ${attachmentNames}` : '');
              if (text) {
                yield { kind: evt.senderKind === 'bot' ? 'bot' : 'agent', text, messageId: evt.messageId, senderName: evt.senderName };
              }
            } else {
              yield {
                kind: 'system', text: evt.text ?? '', category: evt.liveCategory ?? 'system',
                messageId: evt.messageId, senderName: evt.senderName, recoveryState: evt.recoveryState,
              };
            }
          } else if (evt.type === 'done') {
            if (evt.mode === 'ended') {
              yield {
                kind: 'ended',
                messageId: evt.messageId,
                senderName: evt.senderName,
                text: evt.text ?? '',
                category: 'ended',
                ...(evt.postChatSurveyUrl ? { surveyUrl: evt.postChatSurveyUrl } : {}),
              };
              ended = true;
            } else if (evt.mode === 'ai') {
              yield { kind: 'ended', text: '', category: 'returned-to-ai', messageId: evt.messageId, senderName: evt.senderName };
              ended = true;
            } else if (evt.retryMessage) {
              // Live poll turn completed but the customer's last message wasn't
              // delivered (OC session warming on another replica). Tell the widget
              // to re-send it once; keep polling.
              yield { kind: 'retry', text: '' };
            }
            // mode 'live' (or unset) => this poll turn completed; keep polling.
          } else if (evt.type === 'error') {
            if (evt.error === 'live_agent_unavailable') {
              // The session is still live. Keep its binding and use the empty-poll backoff.
              sawFrame = false;
              break;
            }
            yield { kind: 'ended', text: evt.error ?? '', category: 'error', messageId: evt.messageId, senderName: evt.senderName };
            ended = true;
          }
        }

        if (ended) return;
        if (!sawFrame) await delay(LIVE_EMPTY_POLL_DELAY_MS, signal);
      }
    },

    // Return replies drained by this send as well as those delivered by startLive().
    async sendLive(message: string, conversationId: string, signal?: AbortSignal): Promise<WidgetLiveFrame[]> {
      const res = await fetchChat('/api/v1/chat/stream', {
        method: 'POST',
        headers: await chatHeaders(conversationId),
        body: JSON.stringify({ message, conversationId }),
        signal,
      });
      if (!res.ok) {
        throw new Error(`live send ${res.status}: ${await res.text()}`);
      }
      // Sends may drain the same live thread as polls. Preserve these frames;
      // the widget's shared ID gate handles replay on either response.
      const frames: WidgetLiveFrame[] = [];
      for await (const evt of readFoundryEvents(res, signal)) {
        if (evt.type === 'error' && evt.error === 'live_agent_unavailable') {
          throw Object.assign(new Error(evt.text || 'Live agent is not available right now.'), { code: evt.error });
        }
        for (const chunk of chunksFor(evt)) {
          if (chunk.type === 'live') frames.push({
            kind: chunk.liveKind ?? 'system', text: chunk.data, category: chunk.liveCategory,
            messageId: chunk.messageId, senderName: chunk.senderName,
            recoveryState: chunk.recoveryState,
          });
        }
        if (evt.type === 'done' && evt.mode === 'ended') frames.push({
          kind: 'ended', text: evt.text ?? '', category: 'ended', messageId: evt.messageId,
          senderName: evt.senderName,
          surveyUrl: evt.postChatSurveyUrl,
        });
      }
      return frames;
    },

    // Submit pre-chat survey answers. Mirrors the built-in chatClient: the answers
    // are POSTed as a normal /chat/stream turn (message = JSON of the answers),
    // which the human-agent client uses to start the server-owned session. The
    // queue notices come back on THIS response, so yield them before the widget
    // resumes live polling via startLive().
    async *submitPreChatSurvey(
      answers: Record<string, unknown>,
      conversationId: string,
      signal?: AbortSignal,
    ): AsyncGenerator<WidgetLiveFrame> {
      const res = await fetchChat('/api/v1/chat/stream', {
        method: 'POST',
        headers: await chatHeaders(conversationId),
        body: JSON.stringify({ message: JSON.stringify(answers), conversationId }),
        signal,
      });
      if (!res.ok) {
        throw new Error(`pre-chat survey ${res.status}: ${await res.text()}`);
      }
      for await (const evt of readFoundryEvents(res, signal)) {
        if (evt.type === 'error' && evt.error === 'live_agent_unavailable') {
          throw Object.assign(new Error(evt.text || 'Live agent is not available right now.'), { code: evt.error });
        }
        if (evt.type === 'token' && evt.mode === 'live') {
          for (const chunk of chunksFor(evt)) {
            if (chunk.type === 'live') yield {
              kind: chunk.liveKind ?? 'system', text: chunk.data, category: chunk.liveCategory,
              messageId: chunk.messageId, senderName: chunk.senderName,
              recoveryState: chunk.recoveryState,
            };
          }
        } else if (evt.type === 'done' && evt.mode === 'ended') {
          yield {
            kind: 'ended', text: evt.text ?? '', category: 'ended',
            messageId: evt.messageId, senderName: evt.senderName,
            ...(evt.postChatSurveyUrl ? { surveyUrl: evt.postChatSurveyUrl } : {}),
          };
        }
      }
    },
  }, {
    getHeaders: async id => {
      const headers = await chatHeaders(id);
      const auth = await getAuthArgs();
      delete headers.Authorization;
      if (auth) headers.Authorization = `Bearer ${auth.authBearer}`;
      return headers;
    },
    getContext: collectClientContext,
    getIdentity: async () => {
      // A retained coordinator can outlive this lookup's initialization. Do not
      // let a stale owner reach it after the host renews the conversation signal.
      const signal = conversationSignal;
      signal.throwIfAborted();
      const accountId = (await getAuthArgs())?.homeAccountId;
      signal.throwIfAborted();
      return accountId ? `user:${accountId}` : `anonymous:${getAnonSessionId()}`;
    },
    });
  }
}
